import { useSyncExternalStore } from "react";
import { forgetIdentity } from "@/shared/api/identity";
import {
  type ClientListFault,
  fetchClientIdentities,
  withClientTotals,
} from "@/features/clients/api/client-list";
import type { ClientListItem, ClientListResponse } from "@/domain/clients/list-types";

export type SessionFault = ClientListFault;

export interface ClientRowsState {
  rows: ClientListItem[] | null;
  totals: boolean;
  totalsMissing: string[];
  totalsFailed: boolean;
  fault: SessionFault | null;
  failed: boolean;
  endedIds: string[];
  endedIcIds: string[];
  loading: boolean;
}

export type SessionRecover = () => Promise<unknown>;

export function ensureClientRows(recover: SessionRecover | null = null): Promise<void> {
  if (identitiesInFlight) return identitiesInFlight;
  const fresh = state.rows !== null && Date.now() - identitiesAt < CLIENT_ROWS_TTL_MS;
  if (fresh && state.fault === null) return Promise.resolve();
  setState({ ...state, loading: true });
  const flight = readIdentities(recover, true).finally(() => {
    identitiesInFlight = null;
  });
  identitiesInFlight = flight;
  return flight;
}

export function ensureClientTotals(recover: SessionRecover | null = null): Promise<void> {
  if (totalsInFlight) return totalsInFlight;
  const flight = (async () => {
    await ensureClientRows(recover);
    let recovery = recover;
    for (;;) {
      if (state.rows === null || state.fault !== null) return;
      const stale = !state.totals || Date.now() - totalsAt >= CLIENT_ROWS_TTL_MS;
      if (!stale && state.totalsMissing.length === 0) return;
      const rows = state.rows;
      const missing = new Set(state.totalsMissing);
      const asked = stale ? rows : rows.filter((row) => missing.has(rowKey(row)));
      if (state.totalsFailed) setState({ ...state, totalsFailed: false });
      const outcome = await withClientTotals(asked);
      if (state.rows !== rows) continue;
      if (!outcome.ok && outcome.fault !== null && recovery) {
        await recoverSession(recovery);
        recovery = null;
        continue;
      }
      if (!outcome.ok) {
        setState({ ...state, totalsFailed: true, fault: outcome.fault ?? state.fault });
        return;
      }
      if (stale) totalsAt = Date.now();
      const read = new Map(outcome.value.map((row) => [rowKey(row), row]));
      setState({
        ...state,
        rows: rows.map((row) => read.get(rowKey(row)) ?? row),
        totals: true,
        totalsMissing: outcome.missing.map(rowKey),
        totalsFailed: false,
      });
      return;
    }
  })().finally(() => {
    totalsInFlight = null;
  });
  totalsInFlight = flight;
  return flight;
}

export function invalidateClientRows(): void {
  identitiesAt = 0;
  totalsAt = 0;
}

export function clientRowsNow(): ClientRowsState {
  return state;
}

export function clientRowsEverLoaded(): boolean {
  return everLoaded;
}

export function clientIdentitiesNow(): ClientListResponse | null {
  return state.rows === null
    ? null
    : { clients: state.rows, endedIds: state.endedIds, endedIcIds: state.endedIcIds };
}

export function clientIdentitiesInFlight(): Promise<void> | null {
  return identitiesInFlight;
}

export function useClientRows(): ClientRowsState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => INITIAL,
  );
}

async function readIdentities(recover: SessionRecover | null, allowRecovery: boolean) {
  const outcome = await fetchClientIdentities();
  if (outcome.ok) {
    identitiesAt = Date.now();
    everLoaded = true;
    const known = new Map((state.rows ?? []).map((row) => [rowKey(row), row]));
    const totals = state.totals && outcome.value.clients.every((row) => known.has(rowKey(row)));
    const present = new Set(outcome.value.clients.map(rowKey));
    setState({
      rows: outcome.value.clients.map((row) => withKnownTotals(row, known)),
      totals,
      totalsMissing: totals ? state.totalsMissing.filter((key) => present.has(key)) : [],
      totalsFailed: false,
      endedIds: outcome.value.endedIds,
      endedIcIds: outcome.value.endedIcIds,
      fault: null,
      failed: false,
      loading: false,
    });
    return;
  }
  if (outcome.fault) {
    if (allowRecovery && recover) {
      await recoverSession(recover);
      await readIdentities(recover, false);
      return;
    }
    setState({ ...state, fault: outcome.fault, failed: false, loading: false });
    return;
  }
  setState({ ...state, rows: state.rows ?? [], fault: null, failed: true, loading: false });
}

function rowKey(row: Pick<ClientListItem, "kind" | "id">): string {
  return `${row.kind}:${row.id}`;
}

function withKnownTotals(
  row: ClientListItem,
  known: ReadonlyMap<string, ClientListItem>,
): ClientListItem {
  const before = known.get(rowKey(row));
  if (!before) return row;
  return {
    ...row,
    totalValue: before.totalValue,
    returnPct: before.returnPct,
    volatilityPct: before.volatilityPct,
    cvarPct: before.cvarPct,
    riskBandKey: before.riskBandKey,
    portfolioCount: before.portfolioCount,
    portfolios: before.portfolios,
  };
}

async function recoverSession(recover: SessionRecover): Promise<void> {
  forgetIdentity();
  try {
    await recover();
  } catch {}
}

function setState(next: ClientRowsState) {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const CLIENT_ROWS_TTL_MS = 5 * 60_000;

const INITIAL: ClientRowsState = {
  rows: null,
  totals: false,
  totalsMissing: [],
  totalsFailed: false,
  fault: null,
  failed: false,
  endedIds: [],
  endedIcIds: [],
  loading: false,
};

let state: ClientRowsState = INITIAL;
let identitiesAt = 0;
let totalsAt = 0;
let identitiesInFlight: Promise<void> | null = null;
let totalsInFlight: Promise<void> | null = null;
let everLoaded = false;

const listeners = new Set<() => void>();
